<script setup lang="ts">
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/vue/24/outline'
import { computed, useAttrs } from 'vue'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import { AlertType } from '@/constants/ui'
import { translationFunctionCommon } from '@/utilities/translations'

import ButtonAlertPrimary from '../buttons/ButtonAlertPrimary.vue'
import ButtonAlertSecondary from '../buttons/ButtonAlertSecondary.vue'

const props = defineProps<{
  alertType: AlertType
  cancelLabel?: string | undefined
  ctaDisabled?: boolean
  ctaLabel?: string | undefined
  headerDescription?: string
  headerTitle?: string
  loading?: boolean
  message: string
  messageTitle?: string | undefined
  open: boolean
}>()
const emit = defineEmits<{ onClose: []; onCtaClick: [] }>()

defineSlots<{
  actions?(): unknown
  default?(): string
}>()

const tfCommon = translationFunctionCommon()

const onClose = (): void => {
  emit('onClose')
}
const onCtaClick = (): void => {
  emit('onCtaClick')
}

const alertIcons = {
  [AlertType.danger]: XCircleIcon,
  [AlertType.info]: InformationCircleIcon,
  [AlertType.success]: CheckCircleIcon,
  [AlertType.warning]: ExclamationTriangleIcon,
}

const textColorClasses = {
  [AlertType.danger]: 'text-radical_red-500',
  [AlertType.info]: 'text-lochmara-600',
  [AlertType.success]: 'text-keppel-500',
  [AlertType.warning]: 'text-lightning_yellow-500',
}

const color = computed(() => {
  return textColorClasses[props.alertType]
})

const attributesUse = useAttrs()
</script>

<template>
  <Teleport v-if="open" to="body">
    <div
      class="alert-message-overlay fixed inset-0 z-[10001] flex w-screen items-center justify-center bg-black-950 bg-opacity-50"
      :data-testid="attributesUse['data-testid']"
    >
      <div class="flex w-[40%] flex-col items-start rounded-lg bg-white shadow-md">
        <div v-if="props.headerTitle" class="flex flex-col items-start gap-2 self-stretch p-5">
          <div class="flex items-start gap-2.5 self-stretch">
            <div class="h-6 w-6">
              <component :is="alertIcons[props.alertType]" class="h-6 w-6" :class="color" />
            </div>
            <div class="flex shrink-0 grow basis-0 flex-col items-end gap-5">
              <div class="flex flex-col items-start gap-2.5 self-stretch">
                <div class="flex flex-col items-start gap-1.5 self-stretch">
                  <div :class="`${color} leading-0 self-stretch font-medium`">
                    {{ props.headerTitle }}
                  </div>
                  <div v-if="props.headerDescription" class="self-stretch text-sm text-black-900">
                    {{ props.headerDescription }}
                  </div>
                </div>
                <slot></slot>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="props.headerTitle && (props.message || props.messageTitle)"
          class="h-[1px] w-full bg-black-100"
        ></div>

        <div class="flex flex-col items-start gap-2 self-stretch p-5">
          <div class="flex items-start gap-2.5 self-stretch">
            <template v-if="props.headerTitle">
              <div class="h-6 w-6"></div>
            </template>
            <template v-else>
              <component :is="alertIcons[props.alertType]" class="h-6 w-6" :class="color" />
            </template>
            <div class="flex shrink-0 grow basis-0 flex-col items-end gap-5">
              <div
                v-if="props.message || props.messageTitle"
                class="flex flex-col items-start gap-2.5 self-stretch"
              >
                <div class="flex flex-col items-start gap-1.5 self-stretch">
                  <div :class="`${color} leading-0 self-stretch font-medium`">
                    {{ props.messageTitle }}
                  </div>
                  <div class="self-stretch text-sm text-black-900">
                    {{ props.message }}
                  </div>
                </div>
              </div>
              <div class="flex items-start gap-5">
                <slot name="actions">
                  <ButtonAlertPrimary
                    v-if="props.ctaLabel"
                    :data-testid="TEST_IDS_CREATE.alertMessage.btnCTA"
                    :disabled="props.ctaDisabled"
                    :label="props.ctaLabel"
                    :loading="props.loading"
                    :type="props.alertType"
                    @click="onCtaClick"
                  />
                  <ButtonAlertSecondary
                    :data-testid="TEST_IDS_CREATE.alertMessage.btnDismiss"
                    :label="props.cancelLabel ?? tfCommon('button.cancel')"
                    :type="props.alertType"
                    @click="onClose"
                  />
                </slot>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
